import { FetchPolicy, gql } from '@apollo/client';
import {
  DataType,
  FilterConditionInput,
  FilterInput,
  MetadataColumn,
  OperatorType,
  SortInput,
  SortType,
} from '@bechtel/sr-types';
import {
  IServerSideDatasource,
  IServerSideGetRowsParams,
  SetFilterModel,
  SortModelItem,
  TextFilterModel,
} from 'ag-grid-enterprise';
import { each, filter, find, get, includes, map, trim, upperFirst } from 'lodash';
import { client } from '~/apollo-client';
import { getCommodityRowQueryName } from '~/utilities';
import { QUICK_FILTER_KEY } from './types';
import { getDisplayColumns, getDisplayFieldNameFromColumns, isInValidSearchValue } from './utils';

export type FilterModel = SetFilterModel | TextFilterModel;

export const getOperatorType = (
  filterType: 'set' | 'contains' | 'not_contains' | '>' | '<' | '<=' | '>=' | '!=' | '<>' | '=',
  filterValue?: string,
  dataType?: string
) => {
  let operatorType;
  let filter;

  switch (filterType) {
    case 'set':
      operatorType = OperatorType.IN;
      break;
    case 'not_contains':
      operatorType = OperatorType.NOT_LIKE;
      break;
    case '<':
      operatorType = OperatorType.LESS_THAN;
      break;
    case '>':
      operatorType = OperatorType.GREATER_THAN;
      break;
    case '<=':
      operatorType = OperatorType.LESS_THAN_OR_EQUAL;
      break;
    case '>=':
      operatorType = OperatorType.GREATER_THAN_OR_EQUAL;
      break;
    case '!=':
    case '<>':
      operatorType = OperatorType.NOT_EQUAL_TO;
      break;
    case '=':
      operatorType = OperatorType.EQUAL;
      break;
    case 'contains':
      if (filterValue) {
        //Regex to match >, <, <=, >=, != followed by a set of characters.
        const regex = /(<>|[<>!]=?|!=|=)\s*(.+)/;
        const match = filterValue.match(regex);
        //If length === 3 then a match is found
        if (match?.length === 3) {
          //match[1] has the operator
          operatorType = getOperatorType(match[1] as any)?.operatorType;
          //match[2] is the search value
          filter = match[2];
        } else {
          // When there is no match use the default operator which is "LIKE"
          operatorType = OperatorType.LIKE;
          filter = filterValue;
        }
      } else {
        operatorType = OperatorType.LIKE;
      }
      break;
    default:
      filter = filterValue;
      if (dataType === DataType.NUMBER) {
        operatorType = OperatorType.EQUAL;
        break;
      } else {
        operatorType = OperatorType.LIKE;
      }
      break;
  }

  return { operatorType, filter };
};

//Given a list of Metadata column, this method will build the graphql fields part
const buildFields = (columns: MetadataColumn[]) => {
  const fields = map(columns, (column) => {
    //If GUID column and does not have a relation, do NOT request for them
    if (column.dataType === DataType.GUID && !column.relatedMemberName && column.memberName !== 'id') {
      return;
    }
    if (column.relatedMemberName) {
      return `${column.relatedMemberName}{\n
        id\n
        ${column.relatedMemberDisplayField}
      }`;
    }

    return `${column.memberName}\n`;
  }).join('');
  return fields;
};

const restrictionAllowed = ['cableCode', 'cable', 'equipment', 'equipmentCode', 'jumper', 'raceway', 'racewayCode'];
//Given a component name and a list of columns, this method will build the graphql client query
const buildGqlQuery = (columns: MetadataColumn[], componentName: string) => {
  const fields = restrictionAllowed.includes(componentName)
    ? `${buildFields(columns)}\nisRestricted`
    : buildFields(columns);
  const queryName = upperFirst(componentName);
  const rawQuery = `query search${queryName}Rows($searchInput: SearchInput) {
    search${queryName}s(searchInput: $searchInput) {
      items {
      ${fields}
      }
    }
  }`;

  return gql`
    ${rawQuery}
  `;
};

const buildCountGqlQuery = (componentName: string) => {
  const rawQuery = `query ${componentName}Count($searchInput: SearchInput) {
    ${componentName}Count(searchInput: $searchInput)
  }`;

  return gql`
    ${rawQuery}
  `;
};

//Build gql query filters based on ag-grid filter model
const buildFilterInput = (filterModel: FilterModel | null, columns: MetadataColumn[]): FilterConditionInput[] => {
  if (!filterModel) return [];
  const filters: FilterConditionInput[] = [];

  Object.keys(filterModel).forEach((key) => {
    //Supports only multi filter at this time with floating and set filter
    if (filterModel[key]?.filterType === 'multi') {
      const displayMemberName = getDisplayFieldNameFromColumns(key, columns);

      each(filterModel[key]?.filterModels, (filterItem) => {
        if (filterItem) {
          if (filterItem.filterType === 'set' && !filterItem.values?.length) {
            return; // To handle the scenario, where user unselects all set filters.
          }
          //Trimming to check if only operator is typed in the text
          //Do not apply filters when only operator is present
          if (isInValidSearchValue(trim(filterItem.filter))) {
            return;
          }
          const filterType = filterItem.type ? filterItem.type : filterItem.filterType;
          const { operatorType, filter } = getOperatorType(filterType, filterItem.filter);
          filters.push({
            field: displayMemberName,
            operator: operatorType,
            value: filter ?? filterItem.filter ?? '', //If filter is null - it means filterItem.filter is plain text. Pass it as is to the backend.
            values: filterItem.values,
          });
        }
      });
    } else if (includes(['number', 'text'], filterModel[key]?.filterType)) {
      const displayMemberName = getDisplayFieldNameFromColumns(key, columns);
      const trimmedFieldValue = trim(filterModel[key].filter);
      const column = find(columns, (column: MetadataColumn) => column.memberName === key);
      //Do not apply filters when only operator is present
      if (isInValidSearchValue(trimmedFieldValue)) return;
      const { operatorType, filter } = getOperatorType(
        filterModel[key].type,
        filterModel[key].filter,
        column?.dataType
      );
      filters.push({
        field: displayMemberName,
        operator: operatorType,
        value: String(filter ?? filterModel[key].filter ?? ''),
      });
    }
  });
  return filters;
};

const buildSortInput = (
  sortModel: SortModelItem[] | null,
  columns: MetadataColumn[],
  defaultSort?: SortModelItem[] | null
): SortInput[] | null => {
  const sort = map(sortModel, (sortItem) => {
    const displayMemberName = getDisplayFieldNameFromColumns(sortItem.colId, columns);
    return { field: displayMemberName, sort: sortItem.sort === 'desc' ? SortType.DESC : SortType.ASC };
  });
  //If the user does not sort, use the default sort from metadata
  if (!sort?.length && defaultSort) {
    return map(defaultSort, (sortItem) => {
      const displayMemberName = getDisplayFieldNameFromColumns(sortItem.colId, columns);
      return { field: displayMemberName, sort: sortItem.sort === 'desc' ? SortType.DESC : SortType.ASC };
    });
  }
  return sort;
};

export const getSetFilterValues = async (
  params,
  column,
  memberName,
  filterModel,
  quickFilterColumns,
  filterValueGetter,
  fetchPolicy
) => {
  const result = await queryData(
    [column],
    memberName,
    filterModel,
    [],
    quickFilterColumns,
    null,
    [column.memberName],
    null,
    null,
    fetchPolicy
  );

  if (result?.data) {
    const queryResult = result.data[getCommodityRowQueryName(memberName)];

    params.success(
      map(queryResult.items, (item) => {
        const key = column.relatedMemberDisplayField
          ? `${column.relatedMemberName}.${column.relatedMemberDisplayField}`
          : `${column.memberName}`;
        return filterValueGetter ? filterValueGetter(get(item, key)) : get(item, key);
      })
    );
  }
};

export const queryData = async (
  columns: MetadataColumn[],
  componentName: string,
  filterModel: FilterModel | null,
  sortModel: SortModelItem[] | null,
  quickFilterColumns: MetadataColumn[],
  paginationInput?: { startRow: number | undefined; endRow: number | undefined } | null,
  selectDistinctList?: string[] | null,
  defaultFilter?: FilterInput | null,
  defaultSort?: SortModelItem[] | null,
  fetchPolicy?: FetchPolicy,
  countOnly?: boolean
) => {
  const filterConditions = buildFilterInput(filterModel, columns);
  const gridFilterConditions = filter(filterConditions, (filterCondition: FilterConditionInput) => {
    return filterCondition.field !== QUICK_FILTER_KEY;
  });
  const quickFilterCondition = find(
    filterConditions,
    (filterCondition: FilterConditionInput) => filterCondition.field === QUICK_FILTER_KEY
  );
  const sortFields = buildSortInput(sortModel, columns, defaultSort);
  const query = countOnly ? buildCountGqlQuery(componentName) : buildGqlQuery(columns, componentName);

  if (defaultFilter?.and) {
    gridFilterConditions.push(...defaultFilter.and);
  }

  const orConditions: FilterConditionInput[] = quickFilterCondition
    ? map(quickFilterColumns, (quickFilterColumn) => {
        const field = quickFilterColumn?.relatedMemberName
          ? `${quickFilterColumn.relatedMemberName}.${quickFilterColumn?.relatedMemberDisplayField}`
          : quickFilterColumn?.memberName;
        return {
          field: field,
          operator: quickFilterCondition.operator,
          value: quickFilterCondition.value,
        };
      })
    : [];

  if (defaultFilter?.or) {
    orConditions.push(...defaultFilter.or);
  }

  const result = await client.query({
    query: query,
    variables: {
      searchInput: {
        filterInput: {
          and: gridFilterConditions,
          or: orConditions,
        },
        paginationInput: paginationInput
          ? {
              offset: paginationInput?.startRow,
              limit: (paginationInput?.endRow || 30) - (paginationInput?.startRow || 0),
            }
          : null,
        sortInput: sortFields?.length ? sortFields : [],
        selectDistinctList: selectDistinctList,
      },
    },
    fetchPolicy: fetchPolicy ?? 'cache-first',
  });

  return result;
};

export const createDatasource: (
  componentName: string,
  columns: MetadataColumn[],
  initialRowData: any,
  defaultFilter?: FilterInput,
  defaultSortColumns?: SortModelItem[] | null
) => IServerSideDatasource = (
  componentName: string,
  columns: MetadataColumn[],
  initialRowData: any,
  defaultFilter?: FilterInput,
  defaultSortColumns?: SortModelItem[] | null
) => {
  return {
    getRows: async (params: IServerSideGetRowsParams) => {
      const quickFilterColumns = getDisplayColumns(columns);
      const result = await queryData(
        columns,
        componentName,
        params.request.filterModel,
        params.request.sortModel,
        quickFilterColumns,
        {
          startRow: params.request.startRow,
          endRow: params.request.endRow,
        },
        null,
        defaultFilter,
        defaultSortColumns
      );

      if (result?.data) {
        const queryResult = result.data[getCommodityRowQueryName(componentName)];
        initialRowData.current = [...initialRowData.current, ...queryResult.items];

        params.success({
          rowData: queryResult?.items,
        });
      }
    },
  };
};
